<template>
  <Modal
    :show="show"
    title="Forward Message"
    headerVariant="blue"
    size="md"
    dialogMaxWidth="600px"
    :closeOnBackdrop="true"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    @close="$emit('close')"
  >
    <template #header>
      <div class="forward-header__text">
        <div class="forward-header__icon-container">
          <Icon icon="mdi:share-variant" class="forward-header__icon" />
        </div>
        <div>
          <h2 class="forward-header__title">Forward Message</h2>
          <p class="forward-header__subtitle">
            {{
              channelType === "whatsapp"
                ? "Select a WhatsApp conversation to forward this message to"
                : "Select a conversation to forward this message to"
            }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="forward-header__close"
        @click="$emit('close')"
        aria-label="Close"
      >
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>

    <template #body>
      <div class="forward-body">
        <!-- Search -->
        <div class="forward-body__search">
          <Icon icon="mdi:magnify" class="forward-body__search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search conversations..."
            class="forward-body__search-input"
            @input="handleSearch"
          />
        </div>

        <!-- Conversations List -->
        <div class="forward-body__conversations">
          <div v-if="isLoading" class="forward-body__loading">
            <Icon icon="mdi:loading" class="forward-body__loading-icon" />
            <p>Loading conversations...</p>
          </div>

          <div v-else-if="filteredConversations.length === 0" class="forward-body__empty">
            <Icon icon="mdi:message-outline" class="forward-body__empty-icon" />
            <p v-if="searchQuery">No conversations found</p>
            <p v-else-if="channelType !== 'whatsapp'">
              Forwarding is only available for WhatsApp messages
            </p>
            <p v-else>No WhatsApp conversations available</p>
          </div>

          <div v-else class="forward-body__list" @scroll="handleScroll">
            <div
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="forward-body__item"
              :class="{
                'forward-body__item--selected': selectedConversationId === conversation.id,
              }"
              @click="handleSelect(conversation)"
            >
              <div class="forward-body__item-content">
                <div class="forward-body__item-header">
                  <span class="forward-body__item-name">{{
                    conversation.contactName || "Unknown"
                  }}</span>
                  <span class="forward-body__item-time">{{
                    formatTime(conversation.lastMessageAt)
                  }}</span>
                </div>
                <div class="forward-body__item-preview">
                  <span class="forward-body__item-message">{{
                    conversation.lastMessage || "No messages"
                  }}</span>
                </div>
              </div>
            </div>

            <div
              v-if="hasMore && !isLoadingMore"
              class="forward-body__load-more"
              @click="handleLoadMore"
            >
              Load more conversations
            </div>
            <div v-if="isLoadingMore" class="forward-body__loading-more">
              <Icon icon="mdi:loading" class="forward-body__loading-icon" />
              <p>Loading more...</p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <Button variant="secondary" size="md" @click="$emit('close')" :disabled="isForwarding">
        Cancel
      </Button>
      <Button
        variant="primary"
        size="md"
        icon="mdi:share-variant"
        icon-position="left"
        :loading="isForwarding"
        :disabled="!selectedConversationId || isForwarding"
        @click="handleForward"
      >
        Forward
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Button, Icon, Modal } from "@/components/ui";
import type { Conversation } from "@/dtos/Unipile.dto";
import { computed, ref, watch } from "vue";

interface Props {
  show: boolean;
  conversations: Conversation[];
  channelType?: string;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  isForwarding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  isForwarding: false,
});

const emit = defineEmits<{
  close: [];
  forward: [chatId: string];
  search: [query: string];
  "load-more": [];
}>();

const searchQuery = ref("");
const selectedConversationId = ref<string | null>(null);

const filteredConversations = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.conversations;
  }
  const query = searchQuery.value.toLowerCase().trim();
  return props.conversations.filter((conv) => {
    const name = (conv.contactName || "").toLowerCase();
    const message = (conv.lastMessage || "").toLowerCase();
    return name.includes(query) || message.includes(query);
  });
});

watch(searchQuery, (newQuery) => {
  emit("search", newQuery);
});

watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) {
      selectedConversationId.value = null;
      searchQuery.value = "";
    }
  }
);

const handleSelect = (conversation: Conversation) => {
  selectedConversationId.value = conversation.id;
};

const handleForward = () => {
  if (selectedConversationId.value) {
    emit("forward", selectedConversationId.value);
  }
};

const handleSearch = () => {
  // Search is handled by watch
};

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);
  if (distanceToBottom < 100 && props.hasMore && !props.isLoadingMore && !props.isLoading) {
    emit("load-more");
  }
};

const handleLoadMore = () => {
  if (props.hasMore && !props.isLoadingMore && !props.isLoading) {
    emit("load-more");
  }
};

const formatTime = (timestamp?: string): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
</script>

<style scoped>
.forward-header__text {
  display: flex;
  align-items: center;
  gap: 12px;
}

.forward-header__icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.forward-header__icon {
  font-size: 1.25rem;
  color: #fff;
}

.forward-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.forward-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.forward-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.forward-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.forward-body {
  display: flex;
  flex-direction: column;
  max-height: calc(60vh - 130px);
  overflow: hidden;
}

.forward-body__search {
  position: relative;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.forward-body__search-icon {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 1.25rem;
  pointer-events: none;
}

.forward-body__search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  background: #ffffff;
  transition: all 0.2s;
}

.forward-body__search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.forward-body__conversations {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.forward-body__list {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.forward-body__item {
  padding: 0.625rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f3f4f6;
}

.forward-body__item:hover {
  background: #f9fafb;
}

.forward-body__item--selected {
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
}

.forward-body__item-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.forward-body__item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.forward-body__item-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.forward-body__item-time {
  font-size: 0.75rem;
  color: #6b7280;
  flex-shrink: 0;
}

.forward-body__item-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.forward-body__item-message {
  font-size: 0.8125rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.forward-body__loading,
.forward-body__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #9ca3af;
}

.forward-body__loading-icon {
  font-size: 1.5rem;
  color: #6b7280;
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.forward-body__empty-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.forward-body__load-more {
  padding: 0.75rem 1rem;
  text-align: center;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.forward-body__load-more:hover {
  background: #f9fafb;
}

.forward-body__loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
